import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ReplayIcon from '@material-ui/icons/Replay';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import StopIcon from '@material-ui/icons/Stop';
import { makeStyles } from '@material-ui/core/styles';

import { explainFailure } from '../../../constants/whatsapp';
import { actions as whatsappActions } from '../actions';
import ConfirmDialog from '../components/ConfirmDialog';
import EditBroadcastDialog from '../components/EditBroadcastDialog';
import { NAME as WHATSAPP_NAME } from '../reducer';
import { colors } from '../theme';
import { PageTitle } from '../components/PlatformShell';
import { KYIV_ZONE } from '../kyivTime';

function mapStateToProps(state) {
  return { ...state[WHATSAPP_NAME] };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions }, dispatch);

const ACTIVE = ['scheduled', 'queued', 'sending'];
// Удалить можно всё, кроме того, что прямо сейчас отправляется или стоит в очереди.
const REMOVABLE = ['scheduled', 'paused', 'missed', 'done', 'failed', 'cancelled'];
// Ждёт руки менеджера: остановлена им, или время вышло, пока сервер не работал.
const WAITS_FOR_ME = ['paused', 'missed'];
// Переотправить можно завершённую рассылку, в которой кому-то не ушло.
const canResend = (item) => ['done', 'failed', 'cancelled'].includes(item.status) && item.failed + item.stopped > 0;
// Столько получателей сервер отдаёт за один раз (см. GET /api/whatsapp/broadcasts/:id).
const RECIPIENT_LIMIT = 1000;
const FAST_MS = 2000;
const SLOW_MS = 15000;

// Как рассылка выглядит для менеджера: цвет значка и слово.
const STATUS = {
  scheduled: { label: 'Запланирована', bg: colors.accentSoft, color: colors.accent },
  queued: { label: 'В очереди', bg: colors.warnBg, color: colors.warnText },
  sending: { label: 'Идёт отправка', bg: colors.warnBg, color: colors.warnText },
  done: { label: 'Завершена', bg: colors.okBg, color: colors.okText },
  failed: { label: 'Не отправлена', bg: '#ffebee', color: '#b71c1c' },
  paused: { label: 'Остановлена', bg: colors.mutedBg, color: colors.mutedText },
  missed: { label: 'Пропущена', bg: colors.warnBg, color: colors.warnText },
  cancelled: { label: 'Отменена', bg: colors.mutedBg, color: colors.mutedText },
};

const RECIPIENT_STATUS = {
  sent: { label: 'Отправлено', color: colors.okText },
  failed: { label: 'Ошибка', color: '#b71c1c' },
  skipped: { label: 'Не отправлено', color: colors.warnText },
  unknown: { label: 'Неизвестно', color: colors.warnText },
  pending: { label: 'Ждёт', color: colors.textDim },
  sending: { label: 'Отправляется', color: colors.textDim },
};

// Какие вкладки в подробностях и какой статус просить у сервера.
const DETAIL_TABS = [
  { label: 'Все', status: undefined },
  { label: 'Ошибки', status: 'failed' },
  { label: 'Не отправлено', status: 'skipped' },
  { label: 'Неизвестно', status: 'unknown' },
  { label: 'Журнал', log: true },
];

// Что записано в журнале и как это назвать.
const LOG_EVENT = {
  failed: { label: 'Ошибка отправки', color: '#b71c1c' },
  unknown: { label: 'Неизвестно, дошло ли', color: colors.warnText },
  skipped: { label: 'Не отправлено', color: colors.warnText },
  resend: { label: 'Переотправка', color: colors.accent },
  missed: { label: 'Пропущена', color: colors.warnText },
  paused: { label: 'Остановлена', color: colors.textDim },
};

const whenFull = (iso) =>
  iso
    ? new Date(iso).toLocaleString('uk-UA', {
        timeZone: KYIV_ZONE,
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '';

const useStyles = makeStyles((theme) => ({
  // «Последние 100 отправок» и «Обновить» стоят в строке заголовка страницы, справа.
  toolbar: { display: 'flex', alignItems: 'center' },
  hint: { color: colors.textDim, fontSize: '12px', marginRight: theme.spacing(1) },
  panel: { padding: '8px 8px 16px', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    color: colors.accent,
    fontSize: '12px',
    fontWeight: 600,
    textAlign: 'left',
    padding: '11px 10px',
    backgroundColor: colors.panelAlt,
    whiteSpace: 'nowrap',
  },
  thNum: { textAlign: 'right' },
  td: {
    color: colors.text,
    fontSize: '13px',
    padding: '12px 10px',
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'middle',
  },
  tdNum: { textAlign: 'right', whiteSpace: 'nowrap' },
  rowClickable: { cursor: 'pointer', '&:hover': { backgroundColor: colors.panelAlt } },
  name: { fontWeight: 600 },
  sub: { color: colors.textDim, fontSize: '12px', marginTop: 2 },
  progress: { height: 6, borderRadius: 3, minWidth: 110 },
  progressText: { color: colors.textDim, fontSize: '11px', marginTop: 3 },
  empty: { padding: '44px 20px', textAlign: 'center', color: colors.textDim },
  emptyTitle: { color: colors.text, fontWeight: 700, fontSize: '16px', marginBottom: 6 },
  error: { padding: '12px 16px', borderRadius: 8, backgroundColor: '#ffebee', color: '#b71c1c', marginBottom: 16 },
  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  remove: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: '4px 12px',
    color: colors.textDim,
    borderColor: colors.border,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#ffebee',
      borderColor: '#c62828',
      color: '#c62828',
    },
  },
  seriesTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
    padding: '1px 8px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: colors.accentSoft,
    color: colors.accent,
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  subRow: { backgroundColor: colors.panelAlt },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    maxWidth: 250,
    margin: '0 auto',
  },
  start: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: '4px 12px',
    color: colors.okText,
    borderColor: colors.okText,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: colors.okBg, borderColor: colors.okText },
  },
  edit: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: '4px 12px',
    color: colors.accent,
    borderColor: colors.accent,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: colors.accentSoft, borderColor: colors.accent },
  },
  stop: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: '4px 12px',
    color: '#c62828',
    borderColor: 'rgba(198, 40, 40, 0.45)',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#ffebee',
      borderColor: '#c62828',
    },
  },
  detailHead: { color: colors.textDim, fontSize: '13px', marginBottom: 12, lineHeight: 1.5 },
  // Окно рассылки: поиск под вкладками стоит на месте, прокручивается только таблица.
  searchRow: { display: 'flex', alignItems: 'center', padding: '12px 0 4px' },
  searchCount: { color: colors.textDim, fontSize: '12px', marginLeft: 14, whiteSpace: 'nowrap' },
  scroll: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', marginTop: 4 },
  thSticky: { position: 'sticky', top: 0, zIndex: 1 },
  reason: { color: colors.textDim, fontSize: '12px' },
}));

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString('uk-UA', { timeZone: KYIV_ZONE, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';

const progressOf = (item) =>
  item.total > 0 ? Math.min(100, ((item.sent + item.failed + item.stopped) / item.total) * 100) : 0;

const plural = (n, forms) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};
const sendsLabel = (n) => `${n} ${plural(n, ['отправка', 'отправки', 'отправок'])}`;

// Что показать в одной строке, когда у статусов нет единого значения: главный статус серии.
const STATUS_ORDER = ['sending', 'queued', 'missed', 'scheduled', 'paused', 'failed', 'done', 'cancelled'];

// Рассылки одной серии показываются одной строкой: менеджер создал одну рассылку, а то, что
// она уходит в несколько дат, видно внутри неё. Остальные рассылки остаются по одной строке.
const groupItems = (items) => {
  const groups = [];
  const bySeries = new Map();
  items.forEach((item) => {
    if (!item.seriesId) {
      groups.push({ key: `m${item.id}`, series: false, members: [item] });
      return;
    }
    let group = bySeries.get(item.seriesId);
    if (!group) {
      group = { key: `s${item.seriesId}`, series: true, members: [] };
      bySeries.set(item.seriesId, group);
      groups.push(group);
    }
    group.members.push(item);
  });

  return groups.map((group) => {
    const members = group.members
      .slice()
      .sort((a, b) => (a.seriesIndex || 0) - (b.seriesIndex || 0) || a.id - b.id);
    const sum = (field) => members.reduce((all, member) => all + member[field], 0);
    const waiting = members
      .filter((member) => ['scheduled', 'paused'].includes(member.status) && member.sendAt && !member.startedAt)
      .map((member) => member.sendAt)
      .sort();
    return {
      ...group,
      members,
      first: members[0],
      status: STATUS_ORDER.find((status) => members.some((member) => member.status === status)),
      total: sum('total'),
      sent: sum('sent'),
      failed: sum('failed'),
      stopped: sum('stopped'),
      nextAt: waiting[0] || null,
      finishedCount: members.filter((member) => ['done', 'failed', 'cancelled'].includes(member.status)).length,
      activeCount: members.filter((member) => ACTIVE.includes(member.status)).length,
      pausedCount: members.filter((member) => WAITS_FOR_ME.includes(member.status)).length,
      removableCount: members.filter((member) => REMOVABLE.includes(member.status)).length,
    };
  });
};

function StatusChip({ status }) {
  const info = STATUS[status] || STATUS.done;
  return <Chip size="small" label={info.label} style={{ backgroundColor: info.bg, color: info.color, fontWeight: 600 }} />;
}

// Кнопки действий. Какие из них нужны, решает вызывающий: у серии они относятся ко всей
// серии, у отдельной отправки только к ней.
function Actions({
  classes,
  canStart,
  canEdit,
  canStop,
  canRemove,
  canResend: resendable,
  onStart,
  onEdit,
  onStop,
  onRemove,
  onResend,
}) {
  return (
    <div className={classes.actions}>
      {canStart && (
        <Button
          size="small"
          variant="outlined"
          className={classes.start}
          startIcon={<PlayArrowIcon fontSize="small" />}
          onClick={onStart}
        >
          Запустить
        </Button>
      )}
      {canEdit && (
        <Button
          size="small"
          variant="outlined"
          className={classes.edit}
          startIcon={<EditOutlinedIcon fontSize="small" />}
          onClick={onEdit}
        >
          Изменить
        </Button>
      )}
      {canStop && (
        <Button
          size="small"
          variant="outlined"
          className={classes.stop}
          startIcon={<StopIcon fontSize="small" />}
          onClick={onStop}
        >
          Остановить
        </Button>
      )}
      {resendable && (
        <Button
          size="small"
          variant="outlined"
          className={classes.edit}
          startIcon={<ReplayIcon fontSize="small" />}
          onClick={onResend}
        >
          Переотправить
        </Button>
      )}
      {canRemove && (
        <Button
          size="small"
          variant="outlined"
          className={classes.remove}
          startIcon={<DeleteOutlineIcon fontSize="small" />}
          onClick={onRemove}
        >
          Удалить
        </Button>
      )}
    </div>
  );
}

function History(props) {
  const classes = useStyles();
  const { contacts, templates } = props;

  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  // { item, series, count }: на что нажали. series: действие ко всей серии, count: сколько
  // рассылок оно затронет.
  const [stopTarget, setStopTarget] = useState(null);
  const [startTarget, setStartTarget] = useState(null);
  const [quietTarget, setQuietTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [actionError, setActionError] = useState('');
  const latest = useRef(0);

  const load = useCallback(async () => {
    latest.current += 1;
    const mine = latest.current;
    const result = await props.fetchBroadcasts();
    if (mine !== latest.current) return;
    if (result.ok) {
      setItems(result.data);
      setError('');
    } else {
      setError(result.error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    props.fetchTemplates();
    props.fetchCompanies();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Пока что-то идёт, обновляемся часто; иначе редко.
  const anyActive = Boolean(items && items.some((item) => ACTIVE.includes(item.status)));
  useEffect(() => {
    load();
    const timer = setInterval(load, anyActive ? FAST_MS : SLOW_MS);
    return () => {
      clearInterval(timer);
      latest.current += 1;
    };
  }, [anyActive, load]);

  const titleOf = useMemo(() => {
    const map = {};
    templates.forEach((item) => {
      map[item.templateName] = item.title;
    });
    return (name) => map[name] || name;
  }, [templates]);

  const nameByPhone = useMemo(() => {
    const map = {};
    contacts.forEach((contact) => {
      map[String(contact.phone).replace(/\D/g, '')] = contact.name;
    });
    return map;
  }, [contacts]);

  const companyByPhone = useMemo(() => {
    const map = {};
    contacts.forEach((contact) => {
      map[String(contact.phone).replace(/\D/g, '')] = contact.company;
    });
    return map;
  }, [contacts]);

  const groups = useMemo(() => (items ? groupItems(items) : []), [items]);

  // Остановка срабатывает после текущего сообщения: счётчики доходят до верных значений через
  // секунду-две, поэтому после действия таблица ещё пару раз обновляется быстро.
  const followUps = useRef([]);
  useEffect(
    () => () => {
      followUps.current.forEach(clearTimeout);
    },
    []
  );

  const run = async (action) => {
    setActionError('');
    const result = await action();
    // Ночью сервер просит подтверждение: об этом спрашивает отдельное окно, а не красная строка.
    if (!result.ok && result.code !== 'QUIET_HOURS') setActionError(result.error);
    load();
    followUps.current.push(setTimeout(load, 1500), setTimeout(load, 4000));
    return result;
  };

  const handleStop = () => {
    const { item, series } = stopTarget;
    setStopTarget(null);
    run(() => props.pauseBroadcast(item.id, series));
  };
  const handleStart = async (allowQuietHours = false) => {
    const { item, series } = allowQuietHours ? quietTarget : startTarget;
    setStartTarget(null);
    setQuietTarget(null);
    const result = await run(() => props.startBroadcast(item.id, series, allowQuietHours));
    if (result && !result.ok && result.code === 'QUIET_HOURS') {
      setQuietTarget({ item, series, message: result.error });
    }
  };
  const handleResend = () => {
    const item = resendTarget;
    setResendTarget(null);
    run(() => props.resendBroadcast(item.id));
  };
  const handleDelete = () => {
    const { item, series } = deleteTarget;
    setDeleteTarget(null);
    if (openId === item.id) setOpenId(null);
    run(() => props.deleteBroadcast(item.id, series));
  };

  const toggle = (key) => setExpanded({ ...expanded, [key]: !expanded[key] });

  // Одна отправка серии: строка под общей строкой серии.
  const renderOccurrence = (group, item) => (
    <tr key={item.id} className={`${classes.rowClickable} ${classes.subRow}`} onClick={() => setOpenId(item.id)}>
      <td className={classes.td} style={{ paddingLeft: 28 }}>
        <div className={classes.name}>
          Отправка {item.seriesIndex} из {item.seriesTotal}
        </div>
        <div className={classes.sub}>
          {item.sendAt ? `Назначена на ${when(item.sendAt)}` : ''}
        </div>
      </td>
      <td className={classes.td}>
        <div className={classes.statusCell}>
          <StatusChip status={item.status} />
          {item.status === 'failed' && item.error && (
            <div className={classes.sub} style={{ maxWidth: 220 }}>
              {item.error}
            </div>
          )}
        </div>
      </td>
      <td className={classes.td}>
        <LinearProgress className={classes.progress} variant="determinate" value={progressOf(item)} />
        <div className={classes.progressText}>
          {item.sent + item.failed + item.stopped} из {item.total}
        </div>
      </td>
      <td className={`${classes.td} ${classes.tdNum}`}>{item.sent}</td>
      <td className={`${classes.td} ${classes.tdNum}`} style={{ color: item.failed ? '#b71c1c' : undefined }}>
        {item.failed}
      </td>
      <td className={`${classes.td} ${classes.tdNum}`}>{item.stopped}</td>
      <td className={classes.td} style={{ textAlign: 'center' }} onClick={(event) => event.stopPropagation()}>
        <Actions
          classes={classes}
          canStart={['scheduled', ...WAITS_FOR_ME].includes(item.status)}
          canStop={ACTIVE.includes(item.status)}
          canRemove={REMOVABLE.includes(item.status)}
          canResend={canResend(item)}
          onResend={() => setResendTarget(item)}
          onStart={() => setStartTarget({ item, series: false, count: 1 })}
          onStop={() => setStopTarget({ item, series: false, count: 1 })}
          onRemove={() => setDeleteTarget({ item, series: false, count: 1 })}
        />
      </td>
    </tr>
  );

  const renderGroup = (group) => {
    const { first } = group;
    const isOpen = Boolean(expanded[group.key]);
    const canStart = group.series ? group.pausedCount > 0 : ['scheduled', ...WAITS_FOR_ME].includes(first.status);
    const canEdit = group.members.some((member) => ['scheduled', ...WAITS_FOR_ME].includes(member.status));

    return (
      <React.Fragment key={group.key}>
        <tr
          className={classes.rowClickable}
          onClick={() => (group.series ? toggle(group.key) : setOpenId(first.id))}
        >
          <td className={classes.td}>
            <div className={classes.name}>{first.name}</div>
            <div className={classes.sub}>
              {titleOf(first.templateName)}
              {first.listName && ` · ${first.listName}`}
            </div>
            <div className={classes.sub}>
              Создана {when(first.createdAt)}
              {first.createdBy && ` · ${first.createdBy}`}
            </div>
            {group.series && (
              <button
                type="button"
                className={classes.seriesTag}
                aria-expanded={isOpen}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(group.key);
                }}
              >
                {isOpen ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
                {sendsLabel(group.members.length)}
                {first.seriesLabel && ` · ${first.seriesLabel}`}
              </button>
            )}
          </td>
          <td className={classes.td}>
            <div className={classes.statusCell}>
              <StatusChip status={group.status} />
              {group.series ? (
                <div className={classes.sub}>
                  Выполнено {group.finishedCount} из {group.members.length}
                  {group.nextAt && ` · следующая ${when(group.nextAt)}`}
                </div>
              ) : (
                <>
                  {first.status === 'scheduled' && <div className={classes.sub}>будет отправлено {when(first.sendAt)}</div>}
                  {first.status === 'missed' && (
                    <div className={classes.sub} style={{ maxWidth: 230 }}>
                      должна была уйти {when(first.sendAt)}, но сервер не работал: сама не уйдёт
                    </div>
                  )}
                  {first.status === 'paused' && (
                    <div className={classes.sub}>
                      {first.sendAt && !first.startedAt ? `по расписанию: ${when(first.sendAt)}` : 'можно продолжить'}
                    </div>
                  )}
                  {first.status === 'failed' && first.error && (
                    <div className={classes.sub} style={{ maxWidth: 220 }}>
                      {first.error}
                    </div>
                  )}
                </>
              )}
            </div>
          </td>
          <td className={classes.td}>
            <LinearProgress className={classes.progress} variant="determinate" value={progressOf(group)} />
            <div className={classes.progressText}>
              {group.sent + group.failed + group.stopped} из {group.total}
            </div>
          </td>
          <td className={`${classes.td} ${classes.tdNum}`}>{group.sent}</td>
          <td className={`${classes.td} ${classes.tdNum}`} style={{ color: group.failed ? '#b71c1c' : undefined }}>
            {group.failed}
          </td>
          <td className={`${classes.td} ${classes.tdNum}`}>{group.stopped}</td>
          <td className={classes.td} style={{ textAlign: 'center' }} onClick={(event) => event.stopPropagation()}>
            <Actions
              classes={classes}
              canStart={canStart}
              canEdit={canEdit}
              canStop={group.activeCount > 0}
              canRemove={group.removableCount > 0}
              canResend={!group.series && canResend(first)}
              onResend={() => setResendTarget(first)}
              onStart={() =>
                setStartTarget({
                  item: group.series ? group.members.find((m) => WAITS_FOR_ME.includes(m.status)) : first,
                  series: group.series,
                  count: group.series ? group.pausedCount : 1,
                })
              }
              onEdit={() => setEditTarget(group.members.find((m) => ['scheduled', ...WAITS_FOR_ME].includes(m.status)))}
              onStop={() =>
                setStopTarget({
                  item: group.series ? group.members.find((m) => ACTIVE.includes(m.status)) : first,
                  series: group.series,
                  count: group.series ? group.activeCount : 1,
                })
              }
              onRemove={() =>
                setDeleteTarget({
                  item: group.series ? group.members.find((m) => REMOVABLE.includes(m.status)) : first,
                  series: group.series,
                  count: group.series ? group.removableCount : 1,
                })
              }
            />
          </td>
        </tr>
        {group.series && isOpen && group.members.map((member) => renderOccurrence(group, member))}
      </React.Fragment>
    );
  };

  const stopSeries = Boolean(stopTarget && stopTarget.series);
  const startSeries = Boolean(startTarget && startTarget.series);
  const deleteSeries = Boolean(deleteTarget && deleteTarget.series);

  return (
    <div>
      <PageTitle
        kicker="ИСТОРИЯ"
        gap={12}
        title="История рассылок"
        subtitle="Что отправлено и запланировано, ход отправки и кому не дошло"
        actions={
          <div className={classes.toolbar}>
            <span className={classes.hint}>
              {anyActive ? 'Обновляется само, пока идёт отправка' : 'Последние 100 отправок'}
            </span>
            <Tooltip title="Обновить">
              <IconButton size="small" onClick={load} aria-label="Обновить">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        }
      />

      {error && <div className={classes.error}>{error}</div>}
      {actionError && <div className={classes.error}>{actionError}</div>}
      {!items && !error && <LinearProgress />}

      {items && items.length === 0 && (
        <Paper className={classes.empty}>
          <div className={classes.emptyTitle}>Рассылок пока не было</div>
          Когда вы отправите или запланируете рассылку, она появится здесь, с ходом отправки и результатом.
        </Paper>
      )}

      {items && items.length > 0 && (
        <Paper className={classes.panel}>
          <div style={{ overflowX: 'auto' }}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Рассылка</th>
                  <th className={classes.th} style={{ textAlign: 'center' }}>
                    Статус
                  </th>
                  <th className={classes.th}>Ход</th>
                  <th className={`${classes.th} ${classes.thNum}`}>Отпр.</th>
                  <th className={`${classes.th} ${classes.thNum}`}>Ошибок</th>
                  <th className={`${classes.th} ${classes.thNum}`}>Не отпр.</th>
                  <th className={classes.th} />
                </tr>
              </thead>
              <tbody>{groups.map(renderGroup)}</tbody>
            </table>
          </div>
        </Paper>
      )}

      <Details
        classes={classes}
        id={openId}
        item={items && items.find((entry) => entry.id === openId)}
        titleOf={titleOf}
        nameByPhone={nameByPhone}
        companyByPhone={companyByPhone}
        fetchBroadcast={props.fetchBroadcast}
        fetchLog={props.fetchBroadcastLog}
        onResend={(item) => setResendTarget(item)}
        onClose={() => setOpenId(null)}
      />

      <ConfirmDialog
        open={Boolean(resendTarget)}
        title="Переотправить?"
        text={
          resendTarget
            ? `Рассылка «${resendTarget.name}»: отправим ещё раз тем, кому не ушло (ошибок: ${resendTarget.failed}, не отправлено: ${resendTarget.stopped}). Отказавшиеся и те, кому запрос согласия повторять нельзя, пропускаются. Тем, у кого статус «Неизвестно», сообщение могло дойти: они могут получить его дважды. Что и когда не ушло, останется в журнале рассылки.`
            : ''
        }
        confirmLabel="Да, переотправить"
        cancelLabel="Нет"
        onConfirm={handleResend}
        onClose={() => setResendTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(stopTarget)}
        title={stopSeries ? 'Остановить серию?' : 'Остановить рассылку?'}
        text={
          stopTarget
            ? stopSeries
              ? `«${stopTarget.item.name}»: остановятся все ещё не завершённые отправки серии (${stopTarget.count}). Что уже отправлено, останется. Потом их можно запустить кнопкой «Запустить».`
              : stopTarget.item.status === 'sending'
              ? `Отправка «${stopTarget.item.name}» остановится после текущего сообщения. Что уже отправлено, останется, остальным сообщения не уйдут, пока вы её не запустите.`
              : `Рассылка «${stopTarget.item.name}» не уйдёт${
                  stopTarget.item.status === 'scheduled' ? ` в ${when(stopTarget.item.sendAt)}` : ''
                }, пока вы её не запустите. Потом её можно запустить кнопкой «Запустить».`
            : ''
        }
        confirmLabel="Да, остановить"
        confirmColor="secondary"
        cancelLabel="Нет, продолжить"
        onConfirm={handleStop}
        onClose={() => setStopTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(startTarget)}
        title={
          startSeries
            ? 'Запустить серию?'
            : startTarget && ['scheduled', 'missed'].includes(startTarget.item.status)
            ? 'Отправить сейчас?'
            : 'Запустить рассылку?'
        }
        text={
          startTarget
            ? startSeries
              ? `«${startTarget.item.name}»: остановленные и пропущенные отправки серии (${startTarget.count}) вернутся в расписание, а те, чьё время уже прошло, уйдут сейчас. Тем, кому сообщение уже ушло, второе не отправляется.`
              : startTarget.item.status === 'missed'
              ? `Рассылка «${startTarget.item.name}» должна была уйти ${when(startTarget.item.sendAt)}, но сервер в это время не работал, и сама она не уйдёт. Отправить её сейчас?`
              : startTarget.item.status === 'scheduled'
              ? `Рассылка «${startTarget.item.name}» уйдёт сразу, не дожидаясь ${when(startTarget.item.sendAt)}.`
              : startTarget.item.sendAt && !startTarget.item.startedAt && new Date(startTarget.item.sendAt) > new Date()
              ? `Рассылка «${startTarget.item.name}» вернётся в расписание и уйдёт ${when(startTarget.item.sendAt)}.`
              : `Рассылка «${startTarget.item.name}» продолжится сейчас. Тем, кому сообщение уже ушло, второе не отправляется.`
            : ''
        }
        confirmLabel={
          startTarget && !startSeries && ['scheduled', 'missed'].includes(startTarget.item.status)
            ? 'Да, отправить'
            : 'Да, запустить'
        }
        cancelLabel="Нет"
        onConfirm={() => handleStart(false)}
        onClose={() => setStartTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(quietTarget)}
        title="Сейчас ночь. Отправить всё равно?"
        text={quietTarget ? quietTarget.message : ''}
        confirmLabel="Да, отправить"
        cancelLabel="Нет"
        onConfirm={() => handleStart(true)}
        onClose={() => setQuietTarget(null)}
      />

      {editTarget && (
        <EditBroadcastDialog
          item={editTarget}
          items={items || []}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={deleteSeries ? 'Удалить серию?' : 'Удалить рассылку?'}
        text={
          deleteTarget
            ? deleteSeries
              ? `Рассылка «${deleteTarget.item.name}» пропадёт из истории вместе со всеми отправками серии (${deleteTarget.count}) и списками получателей, и ещё не отправленные не уйдут. Сами сообщения в разделе «Сообщения» останутся. Вернуть её будет нельзя.`
              : `Рассылка «${deleteTarget.item.name}» пропадёт из истории вместе со списком получателей${
                  ['scheduled', ...WAITS_FOR_ME].includes(deleteTarget.item.status) ? ' и не уйдёт' : ''
                }. Сами сообщения в разделе «Сообщения» останутся. Вернуть её будет нельзя.`
            : ''
        }
        confirmLabel="Да, удалить"
        confirmColor="secondary"
        cancelLabel="Нет, оставить"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

const MARK = { backgroundColor: '#ffeb3b', color: 'inherit', padding: 0, borderRadius: 2 };
// Больше строк, чем это, и окно рассылки становится высоким: таблица прокручивается внутри него.
const TALL_FROM = 8;
// Один и тот же пустой список: так фильтры ниже не пересчитываются зря.
const NO_ROWS = [];

const digitsOf = (value) => String(value || '').replace(/\D/g, '');

// Поиск в окне рассылки: по имени, компании и телефону (цифры в любом виде).
const matchesQuery = (query, name, company, phone) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const digits = digitsOf(needle);
  return (
    String(name || '').toLowerCase().includes(needle) ||
    String(company || '').toLowerCase().includes(needle) ||
    (digits.length > 0 && digitsOf(phone).includes(digits))
  );
};

// Текст, в котором найденное подсвечено.
const highlight = (text, query) => {
  const value = String(text || '');
  const needle = query.trim();
  const at = needle ? value.toLowerCase().indexOf(needle.toLowerCase()) : -1;
  if (at < 0) return value;
  return (
    <>
      {value.slice(0, at)}
      <mark style={MARK}>{value.slice(at, at + needle.length)}</mark>
      {value.slice(at + needle.length)}
    </>
  );
};

// Телефон (цифры) со знаком плюс; подсвечены цифры, которые ввели в поиск.
const highlightPhone = (phone, query) => {
  const digits = digitsOf(query);
  const at = digits ? String(phone).indexOf(digits) : -1;
  if (at < 0) return `+${phone}`;
  return (
    <>
      +{String(phone).slice(0, at)}
      <mark style={MARK}>{String(phone).slice(at, at + digits.length)}</mark>
      {String(phone).slice(at + digits.length)}
    </>
  );
};

// Кому что ушло: список получателей с причинами для тех, кому не ушло.
function Details({ classes, id, item, titleOf, nameByPhone, companyByPhone, fetchBroadcast, fetchLog, onResend, onClose }) {
  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [log, setLog] = useState(null);
  const [query, setQuery] = useState('');
  const showLog = Boolean(DETAIL_TABS[tab].log);
  const status = DETAIL_TABS[tab].status;
  const active = Boolean(item && ACTIVE.includes(item.status));

  useEffect(() => {
    setTab(0);
    setDetail(null);
    setLog(null);
    setQuery('');
  }, [id]);

  // Журнал: что не ушло и когда. Пока рассылка идёт, обновляется.
  useEffect(() => {
    if (!id || !showLog) return undefined;
    let stopped = false;
    const loadLog = async () => {
      const result = await fetchLog(id);
      if (!stopped && result.ok) setLog(result.data);
    };
    loadLog();
    const timer = active ? setInterval(loadLog, FAST_MS) : null;
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
  }, [id, showLog, active, item && item.finishedAt, item && item.failed, item && item.stopped]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return undefined;
    let stopped = false;
    const load = async () => {
      const result = await fetchBroadcast(id, status);
      if (!stopped && result.ok) setDetail(result.data);
    };
    load();
    const timer = active ? setInterval(load, FAST_MS) : null;
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
  }, [id, status, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const recipients = detail ? detail.recipients : NO_ROWS;
  const shownRecipients = useMemo(
    () =>
      recipients.filter((row) =>
        matchesQuery(query, nameByPhone[row.phone], companyByPhone[row.phone], row.phone)
      ),
    [recipients, query, nameByPhone, companyByPhone]
  );
  const logEntries = log || NO_ROWS;
  const shownLog = useMemo(
    () =>
      logEntries.filter((entry) =>
        matchesQuery(query, nameByPhone[entry.phone], companyByPhone[entry.phone], entry.phone)
      ),
    [logEntries, query, nameByPhone, companyByPhone]
  );

  if (!id) return null;
  const header = detail || item;
  const total = showLog ? logEntries.length : recipients.length;
  const shown = showLog ? shownLog.length : shownRecipients.length;
  // Длинный список: окно растягивается и таблица прокручивается внутри него, а шапка и поиск остаются на месте.
  const tall = total > TALL_FROM;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={tall ? { style: { height: '85vh' } } : undefined}>
      <DialogTitle>{header ? header.name : 'Рассылка'}</DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {header && (
          <div className={classes.detailHead}>
            <StatusChip status={header.status} /> &nbsp;{titleOf(header.templateName)}
            {header.listName && ` · ${header.listName}`}
            <br />
            Создана {when(header.createdAt)}
            {header.startedAt && ` · начало ${when(header.startedAt)}`}
            {header.finishedAt && ` · конец ${when(header.finishedAt)}`}
            <br />
            Отправлено {header.sent} из {header.total}
            {header.failed > 0 && ` · ошибок ${header.failed}`}
            {header.stopped > 0 && ` · не отправлено ${header.stopped}`}
            {header.skipped > 0 && ` · не попали в рассылку ${header.skipped}`}
            {header.declined > 0 && ` · отказались ранее ${header.declined}`}
            {header.error && (
              <>
                <br />
                <span style={{ color: '#b71c1c' }}>{header.error}</span>
              </>
            )}
          </div>
        )}

        <Tabs value={tab} indicatorColor="primary" textColor="primary" onChange={(event, value) => setTab(value)}>
          {DETAIL_TABS.map((entry) => (
            <Tab key={entry.label} label={entry.label} />
          ))}
        </Tabs>

        {total > 0 && (
          <div className={classes.searchRow}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="Поиск: имя, компания или телефон"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Очистить поиск" onClick={() => setQuery('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <span className={classes.searchCount}>
              {query.trim() ? `Найдено ${shown} из ${total}` : `Всего ${total}`}
            </span>
          </div>
        )}

        <div className={classes.scroll}>
        {showLog && !log && <LinearProgress style={{ marginTop: 12 }} />}
        {showLog && log && log.length === 0 && (
          <div className={classes.detailHead} style={{ marginTop: 16 }}>
            В журнале пока пусто: ничего не терялось.
          </div>
        )}
        {showLog && log && log.length > 0 && shownLog.length === 0 && (
          <div className={classes.detailHead} style={{ marginTop: 16 }}>
            Ничего не найдено.
          </div>
        )}
        {showLog && log && shownLog.length > 0 && (
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={`${classes.th} ${classes.thSticky}`}>Когда</th>
                <th className={`${classes.th} ${classes.thSticky}`}>Клиент</th>
                <th className={`${classes.th} ${classes.thSticky}`}>Что произошло</th>
                <th className={`${classes.th} ${classes.thSticky}`}>Подробности</th>
              </tr>
            </thead>
            <tbody>
              {shownLog.map((entry) => {
                const info = LOG_EVENT[entry.event] || { label: entry.event, color: colors.text };
                const failure = entry.event === 'failed' ? explainFailure(entry.detail) : null;
                return (
                  <tr key={entry.id}>
                    <td className={classes.td} style={{ whiteSpace: 'nowrap' }}>
                      {whenFull(entry.at)}
                    </td>
                    <td className={classes.td}>
                      {highlight(nameByPhone[entry.phone], query) || '—'}
                      {companyByPhone[entry.phone] && (
                        <div className={classes.reason}>{highlight(companyByPhone[entry.phone], query)}</div>
                      )}
                      <div className={classes.reason}>{entry.phone && highlightPhone(entry.phone, query)}</div>
                    </td>
                    <td className={classes.td} style={{ color: info.color, fontWeight: 600 }}>
                      {info.label}
                    </td>
                    <td className={classes.td}>
                      <div>{failure ? failure.title : entry.detail}</div>
                      {failure && failure.hint && <div className={classes.reason}>{failure.hint}</div>}
                      {failure && <div className={classes.reason}>{entry.detail}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!showLog && !detail && <LinearProgress style={{ marginTop: 12 }} />}
        {!showLog && detail && detail.recipients.length === 0 && (
          <div className={classes.detailHead} style={{ marginTop: 16 }}>
            Здесь никого нет.
          </div>
        )}
        {!showLog && detail && detail.recipients.length > 0 && shownRecipients.length === 0 && (
          <div className={classes.detailHead} style={{ marginTop: 16 }}>
            Ничего не найдено.
          </div>
        )}
        {!showLog && detail && shownRecipients.length > 0 && (
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={`${classes.th} ${classes.thSticky}`}>Клиент</th>
                <th className={`${classes.th} ${classes.thSticky}`}>Телефон</th>
                <th className={`${classes.th} ${classes.thSticky}`}>Статус</th>
                <th className={`${classes.th} ${classes.thSticky}`}>Причина</th>
              </tr>
            </thead>
            <tbody>
              {shownRecipients.map((row) => {
                const info = RECIPIENT_STATUS[row.status] || RECIPIENT_STATUS.pending;
                return (
                  <tr key={row.phone}>
                    <td className={classes.td}>
                      {highlight(nameByPhone[row.phone], query) || '—'}
                      {companyByPhone[row.phone] && (
                        <div className={classes.reason}>{highlight(companyByPhone[row.phone], query)}</div>
                      )}
                    </td>
                    <td className={classes.td} style={{ whiteSpace: 'nowrap' }}>
                      {highlightPhone(row.phone, query)}
                    </td>
                    <td className={classes.td} style={{ color: info.color, fontWeight: 600 }}>
                      {info.label}
                    </td>
                    <td className={classes.td}>
                      {row.error && (
                        <>
                          <div>{row.status === 'failed' ? explainFailure(row.error).title : row.error}</div>
                          {row.status === 'failed' && explainFailure(row.error).hint && (
                            <div className={classes.reason}>{explainFailure(row.error).hint}</div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!showLog && detail && detail.recipients.length >= RECIPIENT_LIMIT && detail.total > detail.recipients.length && (
          <div className={classes.reason} style={{ marginTop: 10 }}>
            Показаны первые {detail.recipients.length} из {detail.total}.
          </div>
        )}
        </div>
      </DialogContent>
      <DialogActions>
        {header && canResend(header) && (
          <Button
            variant="outlined"
            className={classes.edit}
            startIcon={<ReplayIcon fontSize="small" />}
            onClick={() => onResend(header)}
          >
            Переотправить
          </Button>
        )}
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(History);
